import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import CustomerShell from '../../components/layout/CustomerShell';
import OrderDetails from './OrderDetails';
import ProfileSettings from './ProfileSettings';
import Addresses from './Addresses';
import Support from './Support';
import Notifications from './Notifications';
import Appointments from './Appointments';
import './CustomerDashboard.css';

const CustomerDashboard = () => {
  const { customer, token, isAuthenticated, loading: authLoading, logout, refreshCustomerData } = useCustomerAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      if (isAuthenticated) {
        await fetchDashboardData();
        return;
      }

      if (!authLoading) {
        // Don't use localStorage for session management
        const sessionId = null;
        if (sessionId) {
          const refreshed = await refreshCustomerData();
          if (refreshed) {
            await fetchDashboardData();
            return;
          }
        }
        navigate('/customer/login');
      }
    };
    run();
  }, [isAuthenticated, authLoading, navigate, refreshCustomerData]);

  const fetchDashboardData = async () => {
    try {
      if (!isAuthenticated) {
        navigate('/customer/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/dashboard/overview`, {
        method: 'GET',
        credentials: 'include', // This sends HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOverview(data.overview);
      } else if (response.status === 401) {
        logout();
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    // The logout function in the context will handle navigation
  };

  if (authLoading || loading) {
    return (
      <div className="customer-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !customer) {
    return (
      <div className="customer-dashboard">
        <div className="error-state">
          <h2>Unable to load dashboard</h2>
          <p>Please try logging in again.</p>
          <button onClick={() => navigate('/customer/login')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <CustomerShell>
      <Routes>
        <Route path="/" element={<OverviewTab overview={overview} customer={customer} />} />
        <Route path="/orders" element={<OrdersTab />} />
        <Route path="/orders/:orderId" element={<OrderDetails />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/cart" element={<CartTab />} />
        <Route path="/wishlist" element={<WishlistTab />} />
        <Route path="/downloads" element={<DigitalLibraryTab />} />
        <Route path="/addresses" element={<Addresses />} />
        <Route path="/profile" element={<ProfileSettings />} />
        <Route path="/support" element={<Support />} />
        <Route path="/notifications" element={<Notifications />} />
      </Routes>
    </CustomerShell>
  );
};

// Overview Tab Component
const OverviewTab = ({ overview, customer }) => {
  const navigate = useNavigate();
  if (!overview) return <div>Loading overview...</div>;

  return (
    <div className="overview-tab">
      <div className="overview-grid">
        {/* Quick Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Orders</h3>
            <p className="stat-number">{overview.totalOrders}</p>
          </div>
          <div className="stat-card">
            <h3>Total Spent</h3>
            <p className="stat-number">₦{overview.totalSpent.toLocaleString()}</p>
          </div>
          <div className="stat-card">
            <h3>Loyalty Points</h3>
            <p className="stat-number">{customer.loyaltyPoints}</p>
          </div>
          <div className="stat-card">
            <h3>Wallet Balance</h3>
            <p className="stat-number">₦{customer.wallet.balance.toLocaleString()}</p>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="recent-orders">
          <h2>Recent Orders</h2>
          {overview.recentOrders.length > 0 ? (
            <div className="orders-list">
              {overview.recentOrders.map((order, index) => (
                <div
                  key={index}
                  className="order-item"
                  onClick={() => navigate(`/customer/dashboard/orders/${order._id || order.id}`)}
                >
                  <div className="order-info">
                    <h4>#{order.orderNumber}</h4>
                    <p>{new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="order-status">
                    <span className={`status-badge ${order.status}`}>
                      {order.status}
                    </span>
                    <p>₦{order.total.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No recent orders</p>
          )}
        </div>

        {/* Sidebar stack: Cart + Wishlist */}
        <div className="sidebar-stack">
          <div className="cart-summary">
            <h2>Shopping Cart</h2>
            {overview.cartSummary.itemCount > 0 ? (
              <div className="cart-info">
                <p>{overview.cartSummary.itemCount} items in cart</p>
                <p>Total: ₦{overview.cartSummary.total.toLocaleString()}</p>
                <button className="btn-primary" onClick={() => navigate('/customer/dashboard/cart')}>View Cart</button>
              </div>
            ) : (
              <p>Your cart is empty</p>
            )}
          </div>

          <div className="wishlist-summary">
            <h2>Wishlist</h2>
            {overview.wishlistSummary.itemCount > 0 ? (
              <div className="wishlist-info">
                <p>{overview.wishlistSummary.itemCount} items in wishlist</p>
                <button className="btn-primary" onClick={() => navigate('/customer/dashboard/wishlist')}>View Wishlist</button>
              </div>
            ) : (
              <p>Your wishlist is empty</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Orders Tab Component
const OrdersTab = () => {
  const { isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/orders`, {
        method: 'GET',
        credentials: 'include', // This sends HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#F59E0B',
      'processing': '#3B82F6',
      'shipped': '#8B5CF6',
      'delivered': '#10B981',
      'cancelled': '#EF4444',
      'refunded': '#6B7280'
    };
    return statusColors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': '⏳',
      'processing': '⚙️',
      'shipped': '🚚',
      'delivered': '✅',
      'cancelled': '❌',
      'refunded': '💰'
    };
    return icons[status] || '❓';
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #F3F4F6',
            borderTop: '4px solid #0D7463',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading orders...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>
            My Orders
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: 0
          }}>
            Track and manage your orders
          </p>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '8px',
          backgroundColor: '#F3F4F6',
          padding: '4px',
          borderRadius: '6px'
        }}>
          {['all', 'pending', 'processing', 'shipped', 'delivered'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '8px 16px',
                backgroundColor: filter === status ? '#FFFFFF' : 'transparent',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: filter === status ? '600' : '500',
                color: filter === status ? '#111827' : '#6B7280',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Orders List */}
      {filteredOrders.length > 0 ? (
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          {filteredOrders.map((order) => (
            <div
              key={order._id}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #E5E7EB',
                cursor: 'pointer',
                transition: 'all 150ms ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#0D7463';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
              onClick={() => navigate(`/customer/dashboard/orders/${order._id}`)}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 4px 0'
                  }}>
                    Order #{order.orderNumber || order._id.slice(-8)}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: 0
                  }}>
                    {new Date(order.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  backgroundColor: `${getStatusColor(order.status)}20`,
                  borderRadius: '6px'
                }}>
                  <span style={{ fontSize: '16px' }}>
                    {getStatusIcon(order.status)}
                  </span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: getStatusColor(order.status),
                    textTransform: 'capitalize'
                  }}>
                    {order.status}
                  </span>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '16px',
                alignItems: 'center'
              }}>
                <div>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '0 0 4px 0'
                  }}>
                    {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                  </p>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0
                  }}>
                    ₦{order.total?.toLocaleString() || '0'}
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customer/dashboard/orders/${order._id}`);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: '#0D7463',
                      border: '1px solid #0D7463',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    View Details
                  </button>
                  
                  {order.status === 'delivered' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle reorder
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#0D7463',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Reorder
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#F3F4F6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
            </svg>
          </div>
          <h2 style={{ color: '#111827', marginBottom: '8px' }}>No orders found</h2>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            {filter === 'all' 
              ? "You haven't placed any orders yet." 
              : `No orders with status "${filter}" found.`
            }
          </p>
          <button
            onClick={() => window.location.href = `${window.location.origin}/?store=kwika-digits`}
            style={{
              padding: '12px 24px',
              backgroundColor: '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Start Shopping
          </button>
        </div>
      )}
    </div>
  );
};

// Cart Tab Component
const CartTab = () => {
  const { isAuthenticated, customer } = useCustomerAuth();
  const [cart, setCart] = useState({ items: [], itemCount: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '/api';
      const storeSlug = 'kwika-digits';
      const resp = await fetch(`${API_BASE}/cart/${storeSlug}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(customer?._id ? { 'x-customer-id': customer._id } : {})
        }
      });

      if (resp.ok) {
        const data = await resp.json();
        const backendCart = data.cart;
        const items = (backendCart?.items || []).map(it => {
          const product = it.product || it.productId;
          return {
            id: it.id || it._id,
            productId: product?._id || product?.id,
            product: product ? {
              id: product._id || product.id,
              name: product.name,
              price: product.price,
              images: product.images,
              stock: product.stock,
              isActive: product.isActive
            } : null,
            quantity: it.quantity,
            itemTotal: (product?.price || 0) * it.quantity,
            addedAt: it.addedAt
          };
        });
        setCart({
          items,
          itemCount: backendCart?.itemCount || items.reduce((a, b) => a + (b.quantity || 0), 0),
          total: backendCart?.totalPrice || items.reduce((a, b) => a + (b.itemTotal || 0), 0)
        });
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = () => {
    if (isAuthenticated) {
      // Redirect to checkout with proper authentication
      window.location.href = `${window.location.origin}/checkout?store=kwika-digits`;
    } else {
      // Redirect to login first
      const base = process.env.REACT_APP_CUSTOMER_BASE_URL || 'http://localhost:3001';
      window.location.href = `${base}/login?redirect=${encodeURIComponent('/checkout?store=kwika-digits')}&store=kwika-digits`;
    }
  };

  if (loading) return <div>Loading cart...</div>;

  return (
    <div className="cart-tab">
      <h2>Shopping Cart</h2>
      {cart.items.length > 0 ? (
        <div className="cart-items">
          {cart.items.map((item) => (
            <div key={item.id} className="cart-item">
              <div className="item-image">
                {item.product?.images?.[0] ? (
                  <img src={item.product.images[0]} alt={item.product.name} />
                ) : (
                  <div className="placeholder-image">No Image</div>
                )}
              </div>
              <div className="item-details">
                <h3>{item.product?.name || 'Product not available'}</h3>
                <p>₦{item.product?.price?.toLocaleString() || '0'}</p>
                <div className="quantity-controls">
                  <button>-</button>
                  <span>{item.quantity}</span>
                  <button>+</button>
                </div>
              </div>
              <div className="item-total">
                <p>₦{item.itemTotal.toLocaleString()}</p>
                <button className="remove-btn">Remove</button>
              </div>
            </div>
          ))}
          <div className="cart-summary">
            <h3>Total: ₦{cart.total.toLocaleString()}</h3>
            <button onClick={handleCheckout} className="btn-primary">
              Proceed to Checkout
            </button>
          </div>
        </div>
      ) : (
        <p>Your cart is empty</p>
      )}
    </div>
  );
};

// Wishlist Tab Component
const WishlistTab = () => {
  const { isAuthenticated, customer } = useCustomerAuth();
  const [wishlist, setWishlist] = useState({ items: [], itemCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/dashboard/wishlist`, {
        method: 'GET',
        credentials: 'include', // This sends HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWishlist(data && data.items ? data : { items: [], itemCount: 0 });
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId) => {
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '/api';
      const storeSlug = 'kwika-digits';
      const resp = await fetch(`${API_BASE}/cart/${storeSlug}/add`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(customer?._id ? { 'x-customer-id': customer._id } : {})
        },
        body: JSON.stringify({ productId, quantity: 1 })
      });

      if (resp.ok) {
        // Optionally refresh wishlist after adding
        await fetchWishlist();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/dashboard/wishlist/remove`, {
        method: 'DELETE',
        credentials: 'include', // This sends HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productId })
      });

      if (response.ok) {
        await fetchWishlist();
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #F3F4F6',
            borderTop: '4px solid #0D7463',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading wishlist...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#111827',
          margin: '0 0 8px 0'
        }}>
          Wishlist
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#6B7280',
          margin: 0
        }}>
          Save items you love for later
        </p>
      </div>

      {wishlist.items && wishlist.items.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px'
        }}>
          {wishlist.items.map((item, index) => (
            <div
              key={item._id || index}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #E5E7EB',
                transition: 'all 150ms ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#0D7463';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: '100%',
                height: '200px',
                backgroundColor: '#F3F4F6',
                borderRadius: '6px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {item.product?.images?.[0] ? (
                  <img
                    src={item.product.images[0]}
                    alt={item.product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    color: '#9CA3AF',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    No Image
                  </div>
                )}
              </div>
              
              <div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 8px 0',
                  lineHeight: '1.4'
                }}>
                  {item.product?.name || 'Product'}
                </h3>
                
                <p style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  margin: '0 0 16px 0'
                }}>
                  ₦{item.product?.price?.toLocaleString() || '0'}
                </p>
                
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => handleAddToCart(item.product?._id || item.product?.id)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: '#0D7463',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Add to Cart
                  </button>
                  
                  <button
                    onClick={() => handleRemoveFromWishlist(item.product?._id || item.product?.id)}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: 'transparent',
                      color: '#DC2626',
                      border: '1px solid #DC2626',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          padding: '40px',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#F3F4F6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>
          <h2 style={{ color: '#111827', marginBottom: '8px' }}>Your wishlist is empty</h2>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            Save items you love for later
          </p>
          <button
            onClick={() => window.location.href = `${window.location.origin}/?store=kwika-digits`}
            style={{
              padding: '12px 24px',
              backgroundColor: '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Start Shopping
          </button>
        </div>
      )}
    </div>
  );
};

// Digital Library Tab Component
const DigitalLibraryTab = () => {
  const { isAuthenticated } = useCustomerAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDigitalLibrary();
    }
  }, [isAuthenticated]);

  const fetchDigitalLibrary = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/digital/library`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching digital library:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (product) => {
    if (!product.isActive) {
      return { text: 'Revoked', color: '#DC2626', bg: '#FEE2E2' };
    }
    if (product.isExpired) {
      return { text: 'Expired', color: '#D97706', bg: '#FEF3C7' };
    }
    if (product.limitReached) {
      return { text: 'Download Limit Reached', color: '#6B7280', bg: '#F3F4F6' };
    }
    return { text: 'Available', color: '#059669', bg: '#D1FAE5' };
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #F3F4F6',
            borderTop: '4px solid #0D7463',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading your digital products...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#111827',
          margin: '0 0 8px 0'
        }}>
          Digital Library
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#6B7280',
          margin: 0
        }}>
          Access your purchased digital products and downloads
        </p>
      </div>

      {products.length > 0 ? (
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          {products.map((product) => {
            const status = getStatusBadge(product);
            const canDownload = product.isActive && !product.isExpired && !product.limitReached;

            return (
              <div
                key={product.id}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #E5E7EB',
                  transition: 'all 150ms ease'
                }}
              >
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start'
                }}>
                  {/* Product Image */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    {product.productImage ? (
                      <img
                        src={product.productImage}
                        alt={product.productName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    )}
                  </div>

                  {/* Product Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: 0
                      }}>
                        {product.productName}
                      </h3>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: status.bg,
                        color: status.color,
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {status.text}
                      </span>
                    </div>

                    {/* Purchase Info */}
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '16px',
                      marginBottom: '12px',
                      fontSize: '13px',
                      color: '#6B7280'
                    }}>
                      <span>Purchased: {new Date(product.createdAt).toLocaleDateString()}</span>
                      {product.maxDownloads && (
                        <span>Downloads: {product.downloadCount}/{product.maxDownloads}</span>
                      )}
                      {product.expiresAt && (
                        <span>Expires: {new Date(product.expiresAt).toLocaleDateString()}</span>
                      )}
                    </div>

                    {/* License Key */}
                    {product.licenseKey && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        padding: '8px 12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '6px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <span style={{ fontSize: '13px', color: '#6B7280' }}>License Key:</span>
                        <code style={{
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          color: '#111827',
                          fontWeight: '500'
                        }}>
                          {product.licenseKey}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(product.licenseKey);
                          }}
                          style={{
                            marginLeft: 'auto',
                            padding: '4px 8px',
                            backgroundColor: 'transparent',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#6B7280',
                            cursor: 'pointer'
                          }}
                        >
                          Copy
                        </button>
                      </div>
                    )}

                    {/* Download Button */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {canDownload ? (
                        <a
                          href={`${process.env.REACT_APP_FRONTEND_URL || ''}/download/${product.accessToken}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            backgroundColor: '#0D7463',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7,10 12,15 17,10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Download
                        </a>
                      ) : (
                        <button
                          disabled
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            backgroundColor: '#E5E7EB',
                            color: '#9CA3AF',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'not-allowed'
                          }}
                        >
                          Download Unavailable
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '40px',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#F3F4F6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </div>
          <h2 style={{ color: '#111827', marginBottom: '8px' }}>No digital products yet</h2>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            Your purchased digital products will appear here
          </p>
          <button
            onClick={() => window.location.href = `${window.location.origin}/?store=kwika-digits`}
            style={{
              padding: '12px 24px',
              backgroundColor: '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Browse Products
          </button>
        </div>
      )}
    </div>
  );
};


export default CustomerDashboard;
